// Canonical catalog of levels an invite can start a visitor in (frontend ESM).
// Single source of truth lives in levels.json; the backend reads the same file
// via levels.cjs so the two layers can't drift. Mirrors the roles.js/roles.cjs
// split.
//
// Only levels that are registered in createScene.js belong here - a key in this
// catalog that isn't registered would fail to load. Custom floor-plan layouts
// from the /admin/levels editor are NOT catalog entries: they live in a second
// id namespace, `layout:<uuid>` (see isLayoutLevelId). Layout ids are only
// format-validated here; existence is checked at runtime (createScene.js
// fetches the layout and falls back to DEFAULT_LEVEL if it is gone).
import levelsData from './levels.json';

export const LEVELS = levelsData.levels;
export const DEFAULT_LEVEL = levelsData.defaultLevel;

/** All valid level keys, e.g. ['futuristic-exhibition']. */
export const LEVEL_KEYS = Object.keys(LEVELS);

/** Prefix of the editor-layout level id namespace (layout:<uuid>). */
export const LAYOUT_LEVEL_PREFIX = 'layout:';

const LAYOUT_LEVEL_ID_RE = /^layout:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether `level` is a known, selectable level key. */
export function isValidLevel(level) {
  return typeof level === 'string' && Object.prototype.hasOwnProperty.call(LEVELS, level);
}

/** Whether `level` is a well-formed editor-layout level id (layout:<uuid>). */
export function isLayoutLevelId(level) {
  return typeof level === 'string' && LAYOUT_LEVEL_ID_RE.test(level);
}

/** The layout UUID inside a layout level id, or null when not a layout id. */
export function parseLayoutLevelId(level) {
  return isLayoutLevelId(level) ? level.slice(LAYOUT_LEVEL_PREFIX.length) : null;
}

/**
 * Catalog level OR well-formed layout id - anything an invite/session may
 * start a visitor in. Layout existence is resolved at load time, fail-soft.
 */
export function isStartableLevel(level) {
  return isValidLevel(level) || isLayoutLevelId(level);
}

/** Human-readable label for a level, falling back to the raw key. */
export function levelLabel(level) {
  return LEVELS[level]?.label || level || '';
}
